import { push } from 'redux-first-history';
import _ from 'lodash';
import { all, takeEvery, takeLatest, put, call } from 'redux-saga/effects';
import actions from './actions';
import { BANDING_TARGET_FIELD, BANDING_TIME_BAND_FIELD } from './banding';
import Api from '../../../../helpers/api';
import { keyByProperty } from '../../../../helpers/core';
import { sendRequest } from '../../../../helpers/saga';
import { logger } from '../../../../helpers/logger';
import notifications from '../../../../redux/notifications/actions';
import { skillsKeysAUS, skillsKeysUSA } from '../../../../helpers/skillMapper';
import { isAUS } from '../../../../helpers/countriesHelpers';
import { t } from '../../../../i18n';

const districtApi = new Api('districts/:id');
const positionsApi = new Api('districts/:id/rates/:rate_id/position_rates');
const exportPositionsApi = new Api('districts/:id/rates/:rate_id/position_rates/export');
const importPositionsApi = new Api('districts/:id/rates/:rate_id/position_rates/import');
const sectorsPositionsApi = new Api('districts/:id/rates/:rate_id/sector_position_rates');
const revertSectorPositionsRatesToBaseRateApi = new Api(
  'districts/:district_id/rates/:rate_id/sector_position_rates/bulk_revert_to_base_rate',
);
const bulkUpdateSectorPositionRatesApi = new Api(
  'districts/:district_id/rates/:rate_id/sector_position_rates/bulk_update',
);
const skillsApi = new Api('positions/list?pageSize=999');
const sectorsApi = new Api('sectors/list?pageSize=999');
const formInfoApi = new Api('districts/form_data');
const sectorPositionsApi = new Api('sectors/:id/positions');
const districtPositionRatesApi = new Api('districts/:district_id/update_district_rates');

function getRecords(result) {
  return (
    (result && result.data && result.data.success && result.data.success.data && result.data.success.data.records) || []
  );
}

function* updatePositionStatus({ payload }) {
  const { accept_applications, district, bookable, bookingAction, recruitingAction } = payload;
  const { id, current_rate, future_rate } = district;
  try {
    const updateStatusApi = new Api((bookingAction || recruitingAction).url.replace('/api/v1/', ''));

    const result = yield call(
      sendRequest,
      updateStatusApi.updateItem,
      { data: { position: { accept_applications, bookable } } },
      notifications.REQUEST_ERROR,
    );

    if (result.data) {
      if (current_rate) {
        yield put(actions.loadCurrentPositions({ column: '', order: '', id, rate_id: current_rate.id }));
      }

      if (future_rate) {
        yield put(actions.loadFuturePositions({ column: '', order: '', id, rate_id: future_rate.id }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

// A row that arrived without these keys must go back without them, or the save clears a mapping
// the user was never shown.
export function withBandingFields(position) {
  if (!(BANDING_TARGET_FIELD in position)) {
    return position;
  }

  return {
    ...position,
    [BANDING_TARGET_FIELD]: position[BANDING_TARGET_FIELD] || null,
    [BANDING_TIME_BAND_FIELD]: position[BANDING_TIME_BAND_FIELD] || null,
    banding_enabled: Boolean(position[BANDING_TARGET_FIELD]),
  };
}

function* convertData(id) {
  let currentPositions = [];
  let futurePositions = [];
  let sectorCurrentPositions = [];
  let sectorFuturePositions = [];
  const sectors = [];

  let { district, sectorsList } = yield all({
    district: call(sendRequest, districtApi.load, { id }, notifications.REQUEST_ERROR),
    sectorsList: call(sendRequest, sectorsApi.load, {}, notifications.REQUEST_ERROR),
  });
  district = district && district.data && district.data.success && district.data.success.data;
  sectorsList = getRecords(sectorsList);

  if (district) {
    const currentPositionsParams = [
      sendRequest,
      positionsApi.multipleParamsGetRequest,
      { id, rate_id: district.current_rate && district.current_rate.id },
      notifications.REQUEST_ERROR,
    ];

    const sectorCurrentPositionRates = [
      sendRequest,
      sectorsPositionsApi.multipleParamsGetRequest,
      { id, rate_id: district.current_rate && district.current_rate.id },
      notifications.REQUEST_ERROR,
    ];

    const futurePositionsParams = [
      sendRequest,
      positionsApi.multipleParamsGetRequest,
      { id, rate_id: district.future_rate && district.future_rate.id },
      notifications.REQUEST_ERROR,
    ];

    const sectorFuturePositionRates = [
      sendRequest,
      sectorsPositionsApi.multipleParamsGetRequest,
      { id, rate_id: district.future_rate && district.future_rate.id },
      notifications.REQUEST_ERROR,
    ];

    if (district.current_rate && district.future_rate) {
      const { current, future, sectorCurrent, sectorFuture } = yield all({
        current: call(...currentPositionsParams),
        future: call(...futurePositionsParams),
        sectorCurrent: call(...sectorCurrentPositionRates),
        sectorFuture: call(...sectorFuturePositionRates),
      });
      currentPositions = getRecords(current);
      sectorCurrentPositions = getRecords(sectorCurrent);
      futurePositions = getRecords(future);
      sectorFuturePositions = getRecords(sectorFuture);
    } else {
      if (district.current_rate) {
        const { current, sectorCurrent } = yield all({
          current: call(...currentPositionsParams),
          sectorCurrent: call(...sectorCurrentPositionRates),
        });
        currentPositions = getRecords(current);
        sectorCurrentPositions = getRecords(sectorCurrent);
      }

      if (district.future_rate) {
        const { future, sectorFuture } = yield all({
          future: call(...futurePositionsParams),
          sectorFuture: call(...sectorFuturePositionRates),
        });
        futurePositions = getRecords(future);
        sectorFuturePositions = getRecords(sectorFuture);
      }
    }
  }

  currentPositions = currentPositions.map(withBandingFields);
  futurePositions = futurePositions.map(withBandingFields);

  for (let g = 0; g < currentPositions.length; g += 1) {
    for (let i = 0; i < sectorCurrentPositions.length; i += 1) {
      const { id: positionId, position_ids, position_rates } = sectorCurrentPositions[i];
      const position = currentPositions[g];

      if (position_ids.includes(position.id)) {
        if (!position_rates.find(item => item.id === position.id)) {
          const newPos = { ...position, availablePosition: true };
          (isAUS() ? skillsKeysAUS : skillsKeysUSA).map(tier => {
            if (newPos[tier]) {
              newPos[tier].sector_id = positionId;
            }

            return tier;
          });
          position_rates.push(newPos);
        }
      }
    }
  }

  for (let g = 0; g < futurePositions.length; g += 1) {
    for (let i = 0; i < sectorFuturePositions.length; i += 1) {
      const { id: positionId, position_ids, position_rates } = sectorFuturePositions[i];
      const position = futurePositions[g];

      if (position_ids.includes(position.id)) {
        if (!position_rates.find(item => item.id === position.id)) {
          const newPos = { ...position, availablePosition: true };
          (isAUS() ? skillsKeysAUS : skillsKeysUSA).map(tier => {
            if (newPos[tier]) {
              newPos[tier].sector_id = positionId;
            }

            return tier;
          });
          position_rates.push(newPos);
        }
      }
    }
  }

  for (let i = 0; i < sectorFuturePositions.length; i += 1) {
    const sector = sectorFuturePositions[i];

    for (let g = 0; g < sectorsList.length; g += 1) {
      if (sectorsList[g].id === sector.id) {
        sector.name = sectorsList[g].name;
        break;
      }
    }
  }

  for (let i = 0; i < sectorCurrentPositions.length; i += 1) {
    const currentPositionsObject = sectorCurrentPositions[i];

    for (let g = 0; g < sectorsList.length; g += 1) {
      if (sectorsList[g].id === currentPositionsObject.id) {
        currentPositionsObject.name = sectorsList[g].name;
        break;
      }
    }

    // eslint-disable-next-line no-shadow
    let futurePositions = {
      position_rates: [],
      id: currentPositionsObject.id,
      name: currentPositionsObject.name,
    };

    for (let j = 0; j < sectorFuturePositions.length; j += 1) {
      if (currentPositionsObject.id === sectorFuturePositions[j].id) {
        futurePositions = sectorFuturePositions[j];
        break;
      }
    }

    sectors.push({
      id: currentPositionsObject.id,
      currentPositions: currentPositionsObject,
      futurePositions,
      loading: false,
      positionsLoaded: true,
      _destroy: 0,
    });
  }

  sectors.map(sector => {
    return ['currentPositions', 'futurePositions'].map(key => {
      if (sector[key] && sector[key].position_rates) {
        sector[key].position_rates = sector[key].position_rates.map(item => ({ ...item }));
      }

      return false;
    });
  });
  currentPositions = currentPositions.map(item => ({ ...item, _destroy: 0 }));
  futurePositions = futurePositions.map(item => ({ ...item, _destroy: 0 }));

  return {
    district,
    currentPositions,
    futurePositions,
    sectors,
    sectorsList,
  };
}

function* loadData(data) {
  try {
    const { id } = data.payload;
    const convertedData = yield* convertData(id);
    const { district, currentPositions, futurePositions, sectors, sectorsList } = convertedData;

    if (district) {
      yield put(
        actions.loadDataSuccess({
          district: {
            ...district,
            currentPositions,
            futurePositions,
            sectors,
          },
          positions: {
            currentPositions,
            futurePositions,
          },
          sectors: sectorsList,
          id,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadFormData() {
  try {
    const { skills, sectors, formInfo } = yield all({
      skills: call(sendRequest, skillsApi.load, {}, notifications.REQUEST_ERROR),
      formInfo: call(sendRequest, formInfoApi.load, {}, notifications.REQUEST_ERROR),
      sectors: call(sendRequest, sectorsApi.load, {}, notifications.REQUEST_ERROR),
    });

    if (formInfo && skills && sectors) {
      yield put(
        actions.loadFormDataSuccess({
          formInfo: formInfo && formInfo.data && formInfo.data.success && formInfo.data.success.data,
          skills: getRecords(skills),
          sectors: getRecords(sectors),
        }),
      );
    } else {
      logger.error('Error: states or skills or sectors are undefined...'); // TODO handle error message on GUI
    }
  } catch (error) {
    logger.error(error);
  }
}

// The view page needs only the bands for the mapping hover, and the hover degrades to the target's name without
// them: no loader, and a plain 403 comes back as a status here instead of the generic forbidden toast.
const TIME_BANDS_REQUEST_OPTIONS = { preserveErrorStatus: true };

export function* loadTimeBands() {
  try {
    const formInfo = yield call(sendRequest, formInfoApi.load, {}, null, null, false, TIME_BANDS_REQUEST_OPTIONS);

    // A refusal comes back as `{ status, ... }` with no `data`; a sign-out or a redirect comes back as nothing.
    if (!formInfo?.data) {
      const reason = formInfo?.message ? `: ${formInfo.message}` : '';

      logger.warn(
        formInfo?.status
          ? `districts/form_data refused with ${formInfo.status}${reason}; band windows stay hidden`
          : 'districts/form_data did not answer; band windows stay hidden',
      );

      return;
    }

    const timeBands = formInfo.data.success?.data?.time_bands;

    // The endpoint always sends the key (`[]` when banding is not permitted), so a missing one is a malformed
    // answer, not an empty one.
    if (!timeBands) {
      logger.error('districts/form_data answered without time_bands');

      return;
    }

    yield put(actions.loadTimeBandsSuccess(timeBands));
  } catch (error) {
    logger.error(error);
  }
}

function* sectorRevertToBaseRate(data) {
  const {
    payload: { districtId, position, baseRate },
  } = data;
  const rateId = baseRate.id.value;
  const district_sector_skill_rate_ids = [];

  // Prepare request payload
  (isAUS() ? skillsKeysAUS : skillsKeysUSA).map(
    tier => position[tier] && position[tier].id && district_sector_skill_rate_ids.push(position[tier].id.value),
  );

  try {
    yield call(
      sendRequest,
      revertSectorPositionsRatesToBaseRateApi.multipleParamsCreateItem,
      { data: { district_sector_skill_rate_ids }, params: { district_id: districtId, rate_id: rateId } },
      notifications.REQUEST_ERROR,
    );
  } catch (error) {
    logger.error(error);
  }
}

function* bulkUpdateSectorPositionRates(data) {
  const {
    payload: { districtId, position, baseRate },
  } = data;
  const rateId = baseRate.id.value;
  const district_sector_skill_rates = [];

  // Prepare request payload
  (isAUS() ? skillsKeysAUS : skillsKeysUSA).map(
    tier =>
      position[tier] &&
      position[tier].id &&
      district_sector_skill_rates.push({
        ...(position[tier].id && { id: position[tier].id.value }),
        ...(position[tier].sector_id && { sector_id: position[tier].sector_id.value }),
        ...(position[tier].skill_id && { skill_id: position[tier].skill_id.value }),
        pay: parseFloat(position[tier].pay.value),
        bill: parseFloat(position[tier].bill.value),
      }),
  );

  try {
    if (district_sector_skill_rates.length) {
      yield call(
        sendRequest,
        bulkUpdateSectorPositionRatesApi.multiparamsUpdateItem,
        { data: { district_sector_skill_rates }, params: { district_id: districtId, rate_id: rateId } },
        notifications.REQUEST_ERROR,
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

export function* loadCurrentPositions(data) {
  const { id, rate_id, column, order } = data.payload;
  let currentPositions;

  try {
    currentPositions = yield call(
      sendRequest,
      positionsApi.multipleParamsGetRequest,
      { id, rate_id, column, order },
      notifications.REQUEST_ERROR,
    );
  } catch (error) {
    logger.error(error);
  }

  // Normalised outside the try: a throw in here would reach a catch that dispatches nothing and
  // leaves the table behind a spinner only a reload clears.
  if (currentPositions) {
    yield put(actions.loadCurrentPositionsSuccess(getRecords(currentPositions).map(withBandingFields)));
  }
}

export function* loadFuturePositions(data) {
  const { id, rate_id, column, order } = data.payload;
  let futurePositions;

  try {
    futurePositions = yield call(
      sendRequest,
      positionsApi.multipleParamsGetRequest,
      { id, rate_id, column, order },
      notifications.REQUEST_ERROR,
    );
  } catch (error) {
    logger.error(error);
  }

  // Normalised outside the try: a throw in here would reach a catch that dispatches nothing and
  // leaves the table behind a spinner only a reload clears.
  if (futurePositions) {
    yield put(actions.loadFuturePositionsSuccess(getRecords(futurePositions).map(withBandingFields)));
  }
}

function* stopLoading() {
  yield put(actions.importCsvPositionsError());
}

function* loadSectorPositions({ payload }) {
  try {
    const { id, index } = payload;
    const result = yield call(sendRequest, sectorPositionsApi.load, { id }, notifications.REQUEST_ERROR);

    if (result && result.data && result.data.success && result.data.success.data) {
      yield put(actions.loadSectorPositionsSuccess({ id, data: result.data.success.data, index }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* exportCsvPositions({ payload }) {
  const { id, rate_id } = payload;

  try {
    const result = yield call(
      sendRequest,
      exportPositionsApi.multipleParamsDownloadFile,
      {
        params: {
          id,
          rate_id,
        },
        fileType: 'csv',
      },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      const { fileBlobUrl, fileName } = result.data;
      const link = document.createElement('a');

      link.href = fileBlobUrl;
      link.target = '_blank';
      link.download = fileName || 'positions.csv';
      link.click();

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.exported_successfully', {
            item: t('dash.admin.districts.available_positions'),
          }),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* importCsvPositions({ payload }) {
  const { editImportForm, id, rate_id } = payload;
  const propsArray = Object.values(editImportForm);
  const form = keyByProperty(propsArray, 'name', prop => prop.value);
  const data = new FormData();

  data.append('district_position_rates', form.attachment);

  try {
    const result = yield call(
      sendRequest,
      importPositionsApi.multipleParamsSendFormData,
      { data, params: { id, rate_id } },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      const convertedData = yield* convertData(id);
      const { district, currentPositions, futurePositions, sectors } = convertedData;

      yield put(actions.loadFormData({}));
      yield put(actions.toggleImportModal({ isImportModalVisible: false }));
      yield put(
        actions.edit({
          district: {
            ...district,
            currentPositions,
            futurePositions,
            sectors,
          },
          step: 2,
        }),
      );
      yield put(actions.importCsvPositionsSuccess());
    }
  } catch (error) {
    logger.error(error);
  }
}

function formatRate(rate) {
  const fields = ['id', 'bill', 'min', 'pay', 'valid_since'];

  if (rate && (rate.bill || rate.min || rate.pay || rate.valid_since)) {
    return fields.reduce((acc, item) => {
      acc[item] = rate[item] ? rate[item].value : undefined;

      return acc;
    }, {});
  }
}

function getLevels(keys, position, isSectorPosition) {
  const levels = isAUS() ? skillsKeysAUS : skillsKeysUSA;
  const result = {};

  if (isSectorPosition) {
    keys.push('skill_id');
  }

  levels.map(level => {
    if (position[level]) {
      result[level] = keys.reduce((acc, key) => {
        if (position[level][key]) {
          acc[key] = position[level][key].value;
        }

        return acc;
      }, {});
    }

    if (result[level] && isSectorPosition && position.availablePosition) {
      delete result[level].id;
    }

    return level;
  });

  return result;
}

// Both keys always travel together, and a row on its way out still holds a stale mapping that
// must not reach the payload.
function getBandingMapping(position, destroy) {
  if (!position.banding_enabled || destroy) {
    return {};
  }

  if (!position.banding_enabled.value) {
    return { [BANDING_TARGET_FIELD]: null, [BANDING_TIME_BAND_FIELD]: null };
  }

  return {
    [BANDING_TARGET_FIELD]: (position[BANDING_TARGET_FIELD] && position[BANDING_TARGET_FIELD].value) || null,
    [BANDING_TIME_BAND_FIELD]: (position[BANDING_TIME_BAND_FIELD] && position[BANDING_TIME_BAND_FIELD].value) || null,
  };
}

export function formatPosition(position, isSectorPosition) {
  const keys = ['bill', 'pay', 'id'];

  if (position) {
    // eslint-disable-next-line no-underscore-dangle
    const destroy = position._destroy ? position._destroy.value : 0;

    return {
      id: position.id ? position.id.value : undefined,
      _destroy: destroy,
      // Sector rows copy these fields from the district row, but the mapping is district-level.
      ...(isSectorPosition ? {} : getBandingMapping(position, destroy)),
      ...getLevels(keys, position, isSectorPosition),
    };
  }

  return undefined;
}

// A mapping belongs to the position, not to a rate period, so it travels once. Holes stay put -
// the indexes have to keep matching the form for validation errors to land on the right row.
function formatPositionRates(currentPositions, futurePositions) {
  const current = currentPositions.map(item => formatPosition(item));
  const future = futurePositions.map(item => formatPosition(item));
  // A row with no position chosen yet formats to a blank id, and every such row would match every
  // other one, stripping a mapping off an unrelated future row.
  const covered = new Set(current.filter(rate => rate && rate.id && BANDING_TARGET_FIELD in rate).map(rate => rate.id));

  future.forEach(rate => {
    if (rate && covered.has(rate.id)) {
      delete rate[BANDING_TARGET_FIELD];
      delete rate[BANDING_TIME_BAND_FIELD];
    }
  });

  return { current, future };
}

function formatSectorPosition(sector, position) {
  // eslint-disable-next-line no-underscore-dangle
  return { ...formatPosition(position, true), sector_id: sector.id.value, _destroy: sector._destroy.value };
}

export function formatDistrict(district) {
  const fields = ['name', 'applicable_zip_codes', 'id', 'fulfillment', 'talents_count', 'clients_count'];
  fields.forEach(field => {
    if (district[field]) {
      district[field] = district[field].value;
    } else {
      district[field] = undefined;
    }
  });

  district.state_id = district.state ? district.state.value : undefined;
  district.country_id = district.country ? district.country.value : undefined;

  delete district.state;
  delete district.country;

  if (district.excluded_zip_codes && district.excluded_zip_codes.value) {
    district.excluded_zip_codes = district.excluded_zip_codes.value
      .filter(code => code)
      .map(code => +code)
      .filter(code => typeof code === 'number' && !Number.isNaN(code));
  }

  district.position_rates = formatPositionRates(district.currentPositions, district.futurePositions);
  delete district.currentPositions;
  delete district.futurePositions;

  district.sector_position_rates = { current: [], future: [] };

  district.sectors.map(sector => {
    return (
      sector.currentPositions &&
      sector.currentPositions.position_rates &&
      district.sector_position_rates.current.push(
        ...sector.currentPositions.position_rates.map(item => formatSectorPosition(sector, item)),
      ) &&
      sector.futurePositions &&
      sector.futurePositions.position_rates &&
      district.sector_position_rates.future.push(
        ...sector.futurePositions.position_rates.map(item => formatSectorPosition(sector, item)),
      )
    );
  });

  delete district.sectors;

  ['position_rates', 'sector_position_rates'].map(key => {
    if (!district[key]) {
      return false;
    }

    if (!district[key].current.length) {
      delete district[key].current;
    }

    if (!district[key].future.length) {
      delete district[key].future;
    }

    if (!district[key].future && !district[key].current) {
      delete district[key];
    }

    return true;
  });

  district.rates_attributes = [formatRate(district.current_rate), formatRate(district.future_rate)].filter(
    item => item,
  );
  delete district.current_rate;
  delete district.future_rate;

  if (district.sector_position_rates && district.rates_attributes.length === 1) {
    delete district.sector_position_rates.future;
  }

  delete district.id;
  delete district.fulfillment;
  delete district.talents_count;
  delete district.clients_count;

  return district;
}

function mapValidationsOntoForm(editForm, result) {
  let step = 1;
  const fields = ['name', 'state', 'applicable_zip_codes', 'excluded_zip_codes'];

  if (result.sector_position_rates) {
    step = 3;

    ['current', 'future'].forEach(key => {
      if (!result.sector_position_rates[key]) {
        return;
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const rateKey in result.sector_position_rates[key]) {
        if (Object.prototype.hasOwnProperty.call(result.sector_position_rates[key], rateKey)) {
          const errorData = result.sector_position_rates[key][rateKey];

          if (typeof errorData === 'object') {
            // eslint-disable-next-line no-restricted-syntax
            for (const errorKey in errorData) {
              if (Object.prototype.hasOwnProperty.call(errorData, errorKey)) {
                if (typeof errorData[errorKey] === 'object') {
                  // eslint-disable-next-line no-restricted-syntax
                  for (let subErrorKey in errorData[errorKey]) {
                    if (Object.prototype.hasOwnProperty.call(errorData[errorKey], subErrorKey)) {
                      const positionsKey = key === 'current' ? 'currentPositions' : 'futurePositions';

                      if (subErrorKey === 'skill_id') {
                        subErrorKey = 'id';
                        const [error] = errorData[errorKey].skill_id;
                        editForm.sectors[rateKey][positionsKey].position_rates[0][subErrorKey].error = error;
                      } else {
                        const [error] = errorData[errorKey][subErrorKey];
                        editForm.sectors[rateKey][positionsKey].position_rates[0][errorKey][subErrorKey].error = error;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    });
  }

  const forEachCallback = (subField, rate, index, key, item) => {
    if (item[subField][rate]) {
      step = 2;

      if (!editForm[`${[key]}Positions`][index][subField][rate]) {
        editForm[`${[key]}Positions`][index][subField][rate] = { value: '', error: '', touched: false };
      }
      editForm[`${[key]}Positions`][index][subField][rate].error = _.upperFirst(item[subField][rate][0]);
      editForm[`${[key]}Positions`][index][subField][rate].touched = true;
    }
  };

  const bandingErrorCallback = (index, key, item) => {
    // This fires even when the row carries no `id` error, so without the guard an index with no
    // row behind it would throw and abandon every other error, leaving the form looking clean.
    const position = editForm[`${[key]}Positions`][index];

    if (!position) {
      return;
    }

    [BANDING_TARGET_FIELD, BANDING_TIME_BAND_FIELD].forEach(field => {
      if (!item[field]) {
        return;
      }

      step = 2;

      if (!position[field]) {
        position[field] = { value: null, error: '', touched: false };
      }
      position[field].error = _.upperFirst(item[field][0]);
      position[field].touched = true;
    });
  };

  if (result.position_rates) {
    ['current', 'future'].forEach(key => {
      if (!result.position_rates[key]) {
        return;
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const index in result.position_rates[key]) {
        if (Object.prototype.hasOwnProperty.call(result.position_rates[key], index)) {
          const item = result.position_rates[key][index];

          (isAUS() ? skillsKeysAUS : skillsKeysUSA).forEach(subField => {
            if (item[subField]) {
              ['bill', 'pay'].forEach(rate => {
                forEachCallback(subField, rate, index, key, item);
              });
            }
          });

          if (item.id) {
            step = 2;

            if (!editForm[`${[key]}Positions`][index].id) {
              editForm[`${[key]}Positions`][index].id = { value: '', error: '', touched: false };
            }
            editForm[`${[key]}Positions`][index].id.error = _.upperFirst(item.id[0]);
            editForm[`${[key]}Positions`][index].id.touched = true;
          }

          bandingErrorCallback(index, key, item);
        }
      }
    });
  }

  fields.forEach(field => {
    if (!editForm[field]) {
      editForm[field] = { value: '', error: '', name: field, touched: false };
    }
    editForm[field].error = result[field] ? _.upperFirst(result[field][0]) : '';
    editForm[field].touched = !!editForm[field].error;
  });

  if (result.rates && result.rates.current) {
    ['bill', 'min', 'pay'].forEach(rate => {
      if (!editForm.current_rate[rate]) {
        editForm.current_rate[rate] = { value: '', error: '', name: `current_rate.${rate}`, touched: false };
      }

      if (result.rates.current[rate]) {
        editForm.current_rate[rate].error = _.upperFirst(result.rates.current[rate][0]);
        editForm.current_rate[rate].touched = true;
      }
    });
  }

  if (result.rates && result.rates.future) {
    ['bill', 'min', 'pay'].forEach(rate => {
      if (!editForm.future_rate[rate]) {
        editForm.future_rate[rate] = { value: '', error: '', name: `future_rate.${rate}`, touched: false };
      }

      if (result.rates.future[rate]) {
        editForm.future_rate[rate].error = _.upperFirst(result.rates.future[rate][0]);
        editForm.future_rate[rate].touched = true;
      }
    });
  }

  return { editForm, step };
}

// An unrecognised rejection shape must still leave the user a usable form.
function getDataWithErrors(editForm, result) {
  try {
    return mapValidationsOntoForm(editForm, result);
  } catch (mappingError) {
    mappingError.message = `${mappingError.message} | unmapped district validations: ${JSON.stringify(result)}`;
    logger.error(mappingError);

    return { editForm, step: 2 };
  }
}

export function* addDistrict({ payload }) {
  try {
    const { editForm } = payload;
    const district = formatDistrict({ ...editForm });

    const result = yield call(sendRequest, districtApi.createItem, { data: { district } }, notifications.REQUEST_ERROR);

    if (result) {
      if (result.data) {
        const { id } = result.data;
        yield put(actions.saveSuccess({}));
        yield put(actions.loadData({ id }));
        yield put(push(`/districts/${id}`));
      } else {
        yield put(actions.saveFail(getDataWithErrors({ ...editForm }, result)));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

export function* updateDistrict({ payload }) {
  try {
    const { editForm, id } = payload;
    const district = formatDistrict({ ...editForm });

    const result = yield call(
      sendRequest,
      districtApi.updateItem,
      { data: { district }, id },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      if (result.data) {
        yield put(actions.saveSuccess({}));
        yield put(actions.loadData({ id }));
      } else {
        yield put(actions.saveFail(getDataWithErrors({ ...editForm }, result)));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

export function* onUpdateDistrictPositionsRequest(data) {
  try {
    const {
      payload: { districtId, editForm },
    } = data;
    const district = formatDistrict({ ...editForm });

    const result = yield call(
      sendRequest,
      districtPositionRatesApi.multiparamsUpdateItem,
      {
        data: {
          district: {
            position_rates: formatPositionRates(editForm.currentPositions, editForm.futurePositions),
            rates_attributes: district.rates_attributes,
          },
        },
        params: { district_id: districtId },
      },
      notifications.REQUEST_ERROR,
    );

    // The only save path that sets `loading`, and nothing else clears it, so every failure exit
    // has to dispatch `saveFail` or the user's edits are stranded behind a spinner.
    if (!result) {
      yield put(actions.saveFail({ editForm, step: 2 }));
      // Only `data` separates a saved response from the bare validations map, which is truthy too.
    } else if (result.data) {
      yield put(actions.saveSuccess());
      yield put(actions.loadData({ id: districtId }));
    } else {
      yield put(actions.saveFail(getDataWithErrors({ ...editForm }, result)));
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.saveFail({ editForm: data.payload.editForm, step: 2 }));
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD, loadData),
    takeEvery(actions.SAVE, updateDistrict),
    takeEvery(actions.ADD, addDistrict),
    takeEvery(actions.EDIT, loadFormData),
    takeEvery(actions.UPDATE_POSITIONS_REQUEST, onUpdateDistrictPositionsRequest),
    takeEvery(actions.LOAD_CURRENT_POSITIONS, loadCurrentPositions),
    takeEvery(actions.LOAD_FUTURE_POSITIONS, loadFuturePositions),
    takeEvery(actions.LOAD_SECTOR_POSITIONS, loadSectorPositions),
    takeLatest(actions.LOAD_TIME_BANDS, loadTimeBands),
    takeEvery(actions.EXPORT_CSV_POSITIONS, exportCsvPositions),
    takeEvery(actions.IMPORT_CSV_POSITIONS, importCsvPositions),
    takeEvery(actions.CREATE, loadFormData),
    takeEvery(notifications.REQUEST_ERROR, stopLoading),
    takeEvery(actions.UPDATE_POSITION_STATUS_REQUEST, updatePositionStatus),
    takeEvery(actions.SECTOR_REVERT_TO_BASE_RATE, sectorRevertToBaseRate),
    takeEvery(actions.BULK_UPDATE_SECTOR_POSITION_RATES, bulkUpdateSectorPositionRates),
  ]);
}
